/**
 * Service Firebase Admin pour les opérations privilégiées
 * Utilisé pour modifier les emails des utilisateurs dans Firebase Auth
 * 
 * ✅ Utilise Firebase Cloud Functions pour les opérations privilégiées
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';

// Initialiser Firebase Functions
const functions = getFunctions(app);


/**
 * Modifie l'email d'un utilisateur dans Firebase Auth
 * @param {string} uid - L'UID de l'utilisateur Firebase
 * @param {string} newEmail - Le nouvel email
 * @returns {Promise<Object>} - Les données utilisateur mises à jour
 */
export const updateUserEmail = async (uid, newEmail) => {
  try {
    
    // Validation basique côté client
    if (!uid || typeof uid !== 'string') {
      return {
        success: false,
        error: 'UID utilisateur invalide',
        details: 'L\'UID fourni n\'est pas valide'
      };
    }
    
    if (!newEmail || !isValidEmail(newEmail)) {
      return {
        success: false,
        error: 'Adresse email invalide',
        details: 'Le format de l\'email n\'est pas valide'
      };
    }

    // Appeler la Cloud Function
    const updateEmailFunction = httpsCallable(functions, 'updateUserEmail');
    const result = await updateEmailFunction({ uid, newEmail });
    
    
    return {
      success: true,
      ...result.data
    };
    
  } catch (error) {
    console.error('❌ Erreur lors de l\'appel à la Cloud Function updateUserEmail:', error);
    
    // Gestion des erreurs spécifiques
    let errorMessage = 'Erreur lors de la modification de l\'email';
    
    if (error.code === 'functions/unauthenticated') {
      errorMessage = 'Vous devez être connecté pour effectuer cette action';
    } else if (error.code === 'functions/permission-denied') {
      errorMessage = 'Permissions insuffisantes pour modifier un email';
    } else if (error.code === 'functions/invalid-argument') {
      errorMessage = 'Paramètres invalides pour la modification d\'email';
    // Gestion d'erreur générique pour les autres cas
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      details: error.details || 'Erreur lors de l\'appel à la Cloud Function'
    };
  }
};

/**
 * Révoque tous les tokens de rafraîchissement d'un utilisateur
 * Cela force l'utilisateur à se reconnecter
 * @param {string} uid - L'UID de l'utilisateur Firebase
 * @returns {Promise<Object>} - Résultat de l'opération
 */
export const revokeUserTokens = async (uid) => {
  
  return {
    success: true,
    message: 'La révocation des tokens est automatiquement effectuée lors de la modification d\'email'
  };
};

/**
 * Génère un lien de vérification d'email
 * @param {string} email - L'email pour lequel générer le lien
 * @returns {Promise<Object>} - Le lien de vérification
 */
export const generateEmailVerificationLink = async (email) => {
  try {
    
    if (!email || !isValidEmail(email)) {
      return {
        success: false,
        error: 'Adresse email invalide',
        details: 'Le format de l\'email n\'est pas valide'
      };
    }

    // Appeler la Cloud Function
    const generateLinkFunction = httpsCallable(functions, 'generateEmailVerificationLink');
    const result = await generateLinkFunction({ email });
    
    
    return {
      success: true,
      ...result.data
    };
    
  } catch (error) {
    console.error('❌ Erreur lors de l\'appel à la Cloud Function generateEmailVerificationLink:', error);
    
    return {
      success: false,
      error: error.message || 'Erreur lors de la génération du lien de vérification',
      details: error.details || 'Erreur lors de l\'appel à la Cloud Function'
    };
  }
};

/**
 * Valide le format d'une adresse email
 * @param {string} email - L'email à valider
 * @returns {boolean} - True si l'email est valide
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Vérifie si Firebase Admin est correctement configuré
 * @returns {boolean} - True si configuré
 */
export const isFirebaseAdminConfigured = () => {
  // Firebase Cloud Functions sont disponibles
  return true;
};

// Export par défaut pour faciliter l'importation
export default {
  updateUserEmail,
  revokeUserTokens,
  generateEmailVerificationLink,
  isFirebaseAdminConfigured
};
